<template>
  <section
    :class="{ inner }"
    :style="{ backgroundImage: `url(${getImageFromProxyLocal})` }"
    class="radial-bg-card"
  >
    <div class="top-content">
      <div>
        <h1 class="h1">
          {{ t("inventory.checker.title") }}
        </h1>
        <i18n-t
          v-if="!user"
          class="top-content-subtitle"
          keypath="inventory.checker.desc_unauth"
          tag="p"
        >
          <template #desc_unauth_link>
            <button
              class="green-link"
              type="button"
              @click="login"
            >
              {{ t("inventory.checker.desc_unauth_link") }}
            </button>
          </template>
        </i18n-t>
        <i18n-t
          v-else
          class="top-content-subtitle"
          keypath="inventory.checker.desc_auth"
          tag="p"
        >
          <template #desc_auth_link>
            <nuxt-link
              :to="localePath(`/inventory/${user?.steamid64}`)"
              class="green-link"
              type="button"
            >
              {{ t("inventory.checker.desc_auth_link") }}
            </nuxt-link>
          </template>
        </i18n-t>
      </div>

      <div class="input-wrapper">
        <div
          :class="{ error: showErrorMessage }"
          class="input-wrapper__inner"
        >
          <form
            class="f"
            @submit.prevent="onSubmit"
          >
            <input
              v-model="steamId"
              :disabled="pending"
              placeholder="Steam ID..."
              type="text"
            >

            <button
              :disabled="pending"
              class="btn btn-violet"
              type="submit"
            >
              <span v-if="!pending">{{ t("inventory.checker.btn") }}</span>
              <i
                v-if="pending"
                class="i-loading"
              />
            </button>
          </form>
        </div>
        <p
          v-if="showErrorMessage"
          class="red mt4 f14"
        >
          {{ t('inventory.checker.error') }}
        </p>
      </div>
    </div>
  </section>
</template>

<script lang="ts" setup>
import type { UserFullInfo } from '~/graphql/generated/schema-types';
import { EventBus } from '~/composables/bus';
import { useStore } from '~/store';

defineProps<{
  inner?: boolean
}>();

const pending = ref(false);
const showErrorMessage = ref(false);
const localePath = useLocalePath();
const router = useRouter();
const store = useStore();
const user = computed(() => store.user as UserFullInfo | null);
const config = useRuntimeConfig();
const siteHost = config.public.siteHost;
const url = config.public.baseUrlV2;
const { t } = useI18n();

function login() {
  return (document.location = `${url}/auth/steam`);
}

const getImageFromProxyLocal = computed(() => {
  const sizes = '1240:288';
  const url = `${siteHost}/images/gradient-bg.png`;
  return `https://imageproxy.csmarketcap.com/insecure/rs:fit:${sizes}:0/q:99/g:nowe/f:webp/plain/${url}`;
  // return "/images/gradient-bg.png";
});

const steamId = ref('');

async function onSubmit() {
  if (!steamId.value) {
    return EventBus.$emit('notify', {
      success: false,
      msg: 'Please, enter Steam ID',
    });
  }
  try {
    pending.value = true;
    const { data, errors } = await checkInventoryValue(steamId.value);
    if (errors?.length) {
      EventBus.$emit('notify', {
        success: false,
        msg: errors?.[0].message || 'Error checking inventory',
      });
      throw new Error('Error checking inventory');
    }

    if (!Number.isNaN(Number(data?.checkInventoryValue))) {
      await router.push(localePath(`/inventory/${data?.checkInventoryValue}`));
    }

    if (!data?.checkInventoryValue || data?.checkInventoryValue === 'false') {
      pending.value = false;
      showErrorMessage.value = true;
    }
  } catch {
    pending.value = false;
  }
}

watch(steamId, () => {
  showErrorMessage.value = false;
});

const { $socket } = useNuxtApp();

function subscribeToSocket() {
  if (!$socket) {
    return;
  }

  $socket.on('inventory', (data: any) => {
    if (!Number.isNaN(Number(data)) && pending.value) {
      router.push(localePath(`/inventory/${data}`));
    }
  });
}

function unsubscribeFromSocket() {
  if (!$socket)
    return;
  $socket.off('inventory');
}

onMounted(() => {
  subscribeToSocket();
});

onBeforeUnmount(() => {
  unsubscribeFromSocket();
});
</script>

<style lang="scss" scoped>
.radial-bg-card {
  border-radius: 16px;
  background: var(--gr-primary-10);
  display: flex;
  align-items: center;
  justify-content: center;
  background: url("/images/gradient-bg.png") no-repeat center center;
  -webkit-background-size: cover;
  background-size: cover;

  .top-content {
    text-align: center;
    padding: 60px;
    gap: 32px;
    @media screen and (max-width: 768px) {
      padding: 40px 20px;
    }

    .h1 {
      font-size: 24px;
      font-weight: 600;
      color: var(--accent-on-accent-secondary);
      margin-bottom: 12px;
    }

    &-subtitle {
      color: var(--surfaces-on-surface-variable);
      margin-bottom: 32px;

      .green-link {
        color: var(--accent-primary);
        text-decoration: underline;
        display: inline;
        padding: 0;
        font-weight: inherit;
        font-size: inherit;
      }
    }

    .input-wrapper {
      flex-grow: 1;
      width: 100%;

      &__inner {
        background: #383466;
        border-radius: 12px;
        padding: 12px;
        flex-grow: 1;
        width: 100%;
        border: 1px solid transparent;

        &.error {
          border-color: var(--destructive);
          background: rgba(255, 87, 87, 0.05);
        }
      }

      input {
        background: transparent;
        font-size: 16px;

        &::-webkit-input-placeholder {
          color: var(--surfaces-on-surface); /* Chrome, Safari, Opera */
          opacity: 0.7;
        }

        &::-moz-placeholder {
          color: var(--surfaces-on-surface); /* Firefox 19+ */
          opacity: 0.7;
        }

        input:-ms-input-placeholder {
          color: var(--surfaces-on-surface); /* IE 10+ */
          opacity: 0.7;
        }

        &::-ms-input-placeholder {
          color: var(--surfaces-on-surface); /* Edge */
          opacity: 0.7;
        }

        &::placeholder {
          color: var(--surfaces-on-surface); /* Standard */
          opacity: 0.7;
        }
      }

      .btn {
        min-height: 44px;
        white-space: nowrap;
        font-weight: 600;
        font-size: 14px;
      }

      @media screen and (max-width: 768px) {
        &__inner {
          background: transparent;
          border-radius: 0;
          padding: 0;
        }

        .f {
          flex-direction: column;

          input {
            background: #383466;
            border-radius: 12px;
            padding: 18px 12px 18px 20px;
            flex-grow: 1;
            width: 100%;
            height: unset;
          }

          .btn {
            width: 100%;
            margin-top: 12px;
          }
        }
      }
    }
  }

  &.inner {
    @media screen and (min-width: 989px) {
      .top-content {
        text-align: left;
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 40px;
        width: 100%;

        &-subtitle {
          margin-bottom: 0;
        }

        .input-wrapper {
          max-width: 48%;
        }
      }
    }
  }
}
</style>
